import { ArrowRightOutlined, ExclamationCircleOutlined, VerticalAlignBottomOutlined, VerticalLeftOutlined, VerticalRightOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Radio, Switch, Tooltip, Typography } from "antd";
import { RcFile } from "antd/es/upload";
import mergeClassNames from "common/mergeClassNames";
import { SystemConnectionSelector } from "components";
import DockableContainer from "components/atoms/DockableContainer";
import DataMapper from "components/molecules/DataMapper";
import DataMapperFlow from "components/molecules/DataMapperFlow";
import DataPreview from "components/molecules/DataPreview";
import FileDragger from "components/molecules/FileDragger";
import MappingConfigurationManager from "components/molecules/MappingConfigurationManager";
import { useAppDispatch } from "hooks";
import React from "react";
import { setDataMapperFlowAction } from "store/dataMapperFlow/actions";
import DataObjectBulkUploadLayout, { Footer, Header } from "./DataObjectBulkUploadLayout";

const { Text } = Typography;

interface IColumnDictionary {
    [key: string]: string;
}

type DataObjectBulkUploadProps = {
    bulkUploadSource: unknown;
    dataSet: IDataSet;
    schemaModel: ISchemaModel;
    onCreateDataObjectsBulkUploadJob: (dataUploadOptions: string, file: RcFile | File, fieldMappings: IDataMappingField[]) => void;
    aiMappingLoading: boolean;
    aiMappingSuggestion?: IAiMappingSuggestion | null;
    getAiMappingSuggestionResult?: { success: boolean; error?: any } | null;
    onGetAiMappingSuggestion: (fileReadResult: IFileReadResult<RcFileExtension>) => void;
    onResetAiMapping: () => void;
    mappingConfigurations?: { Items: any[] };
    saveMappingConfigurationResult?: { success: boolean; error?: any };
    getMappingConfigurationsResult?: { success: boolean; error?: any };
    deleteMappingConfigurationResult?: { success: boolean; error?: any };
    onSaveMappingConfiguration?: (businessAreaId: string, schemaId: string, title: string, mappingConfiguration: any) => void;
    onGetMappingConfigurations?: (businessAreaId: string, schemaId: string) => void;
    onDeleteMappingConfiguration?: (businessAreaId: string, schemaId: string, configurationId: string) => void;
};

interface RcFileExtension extends RcFile {
    connectionId?: string;
    connectionType?: string;
    bucket?: string;
    tableName?: string;
}

export default function DataObjectBulkUpload({
    bulkUploadSource,
    dataSet,
    schemaModel,
    onCreateDataObjectsBulkUploadJob,
    aiMappingLoading,
    aiMappingSuggestion,
    getAiMappingSuggestionResult,
    onGetAiMappingSuggestion,
    onResetAiMapping,
    mappingConfigurations = { Items: [] },
    saveMappingConfigurationResult,
    getMappingConfigurationsResult,
    deleteMappingConfigurationResult,
    onSaveMappingConfiguration,
    onGetMappingConfigurations,
    onDeleteMappingConfiguration,
}: DataObjectBulkUploadProps) {
    const dispatch = useAppDispatch();
    const draggerRef = React.useRef<IFileDraggerRef>();
    const [dataPanelPosition, setDataPanelPosition] = React.useState<"bottom" | "left" | "right">("bottom");
    const [showDataTable, setShowDataTable] = React.useState<boolean>(true);
    const [fileReadResult, setFileReadResult] = React.useState<IFileReadResult<RcFileExtension> | null>({
        file: null,
        data: null,
        columns: [],
        error: null,
        meta: {
            fields: [],
        },
    });
    const hasPrimaryKey = React.useMemo(() => schemaModel?.fields?.some((f) => f.isPrimaryKey), [schemaModel]);

    // Map field properties to determine replace disabled status
    const isReplaceDisabled = React.useMemo(() => {
        const hasExistingRecords = (schemaModel as any)?.totalRecordCount > 0 || (dataSet as any)?.totalRecordCount > 0 || (dataSet as any)?.recordCount > 0;
        return hasPrimaryKey && hasExistingRecords;
    }, [hasPrimaryKey, schemaModel, dataSet]);

    const [dataUploadOptions, setDataUploadOptions] = React.useState<string>(isReplaceDisabled ? "APPEND_DATA" : "REPLACE_DATA");

    React.useEffect(() => {
        if (isReplaceDisabled && dataUploadOptions === "REPLACE_DATA") {
            setDataUploadOptions("APPEND_DATA");
        }
    }, [isReplaceDisabled, dataUploadOptions]);

    // Passing mapping to Flow and back
    const [dataColumnMapping, setDataColumnMapping] = React.useState<IMappingSchemaFields>();

    // Store the source data headers
    const columnsDict = React.useRef<IColumnDictionary>();

    const [readingFile, setReadingFile] = React.useState<boolean>(false);
    const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
    const [mappedSchemaColumns, setMappedSchemaColumns] = React.useState<string[]>([]);

    const [showFlowMapper, setShowFlowMapper] = React.useState<boolean>(false);
    const [sampleDataRow, setSampleDataRow] = React.useState<number>(0);

    const onReadingFile = React.useCallback(() => {
        setReadingFile(true);
    }, []);

    const findMappedSchemaColumns = React.useCallback(() => {
        const colMapping = { ...dataColumnMapping };

        // Update the mapped schame columns
        const newColumns: string[] = [];
        Object.values({ ...colMapping })
            .filter((c) => c.sourceColumns && c.sourceColumns.length > 0)
            .forEach((field) => {
                // Construct refreshed columns
                if (field.sourceColumns && field.sourceColumns.length > 0) {
                    // Mapped multiple sources to target
                    field.sourceColumns?.forEach((col) => {
                        if (columnsDict.current && columnsDict.current[col]) {
                            // The field must exists
                            newColumns.push(col);
                        }
                    });
                }
            });
        newColumns.sort();
        if (JSON.stringify(mappedSchemaColumns) !== JSON.stringify(newColumns)) {
            setMappedSchemaColumns(newColumns);
        }
    }, [dataColumnMapping, mappedSchemaColumns]);

    React.useEffect(() => {
        findMappedSchemaColumns();
    }, [findMappedSchemaColumns]);

    const readFileComplete = React.useCallback(
        (result: IFileReadResult<RcFileExtension>) => {
            const colMapping: IMappingSchemaFields = {};
            const matchedColumns: string[] = [];
            const unmatchedColumns: string[] = [];

            if (result.columns && result.columns.length > 0) {
                columnsDict.current = result.columns.reduce((dict, column) => {
                    dict[column] = column;
                    return dict;
                }, {} as IColumnDictionary);

                result.columns.forEach((c) => {
                    // Find all unmatched columns to be appended to the back
                    if (!schemaModel.fields.some((f) => f.name === c)) {
                        unmatchedColumns.push(c);
                    }
                });
            }

            for (let field of schemaModel.fields) {
                if (columnsDict.current && columnsDict.current[field.name]) {
                    // Field exists in the source column
                    colMapping[field.name] = {
                        mappingType: "DATA_COLUMN",
                        value: columnsDict.current[field.name],
                        fieldId: field.fieldId,
                        name: field.name,
                        dataType: field.dataType,
                        isRequired: field.isRequired,
                        isPrimaryKey: field.isPrimaryKey,
                        sourceColumns: [columnsDict.current[field.name]],
                    };
                    // Order the columns by schema fields
                    matchedColumns.push(field.name);
                } else {
                    colMapping[field.name] = {
                        mappingType: "DATA_COLUMN",
                        value: "",
                        fieldId: field.fieldId,
                        name: field.name,
                        dataType: field.dataType,
                        isRequired: field.isRequired,
                        isPrimaryKey: field.isPrimaryKey,
                    };
                }
            }

            setFileReadResult({
                ...result,
                // Appending unmatched columns to the right of the table
                columns: matchedColumns.concat(unmatchedColumns),
            });
            setDataColumnMapping(colMapping);
            setReadingFile(false);
        },
        [schemaModel.fields],
    );

    const readFileError = React.useCallback((result: IFileReadResult<RcFileExtension>) => {
        setFileReadResult(result);
        setReadingFile(false);
    }, []);

    const cancelUpload = React.useCallback(() => {
        setReadingFile(false);
        setFileReadResult(null);
        setValidationErrors([]);
        setDataColumnMapping(undefined);
        if (draggerRef.current) {
            draggerRef.current.clearFile();
        }
    }, []);

    const isUploadAllowed = React.useMemo(() => {
        if (!fileReadResult || fileReadResult.error || readingFile) return false;
        if (fileReadResult.file) return true;
        return Array.isArray(fileReadResult.data) && fileReadResult.data.length > 0;
    }, [fileReadResult, readingFile]);

    const unmappedMandatoryFields = React.useMemo(() => {
        if (!dataColumnMapping) return [];
        return schemaModel.fields.filter((f) => (f.isPrimaryKey || f.isRequired) && !dataColumnMapping[f.name]?.value).map((f) => f.name);
    }, [dataColumnMapping, schemaModel.fields]);

    const validateData = React.useCallback(() => {
        let validatedErrors: string[] = [];
        let validUploadColumnCount = 0;

        if (dataColumnMapping) {
            for (let field of schemaModel.fields) {
                if (field.isPrimaryKey && (!dataColumnMapping[field.name] || !dataColumnMapping[field.name].value)) {
                    validatedErrors.push(`No data column for primary key field '${field.name}'`);
                } else if (field.isRequired && (!dataColumnMapping[field.name] || !dataColumnMapping[field.name].value)) {
                    validatedErrors.push(`No data column for required field '${field.name}'`);
                } else if (dataColumnMapping[field.name] && dataColumnMapping[field.name].mappingType === "FORMULA" && !dataColumnMapping[field.name].value) {
                    validatedErrors.push(`Formula expression is empty for field '${field.name}'`);
                } else if (dataColumnMapping[field.name] && dataColumnMapping[field.name].value) {
                    validUploadColumnCount++;
                }
            }

            if (validUploadColumnCount === 0 && validatedErrors.length === 0) {
                validatedErrors.push("There are no data columns to upload for schema fields.");
            }
        } else {
            validatedErrors.push("There are no data columns to upload for schema fields.");
        }

        setValidationErrors(validatedErrors);
        return validatedErrors.length === 0;
    }, [dataColumnMapping, schemaModel.fields]);

    const handleGetAiMappingSuggestion = React.useCallback(() => {
        if (!fileReadResult || !fileReadResult.data || !fileReadResult.columns) return;
        Modal.confirm({
            title: "Apply AI Mapping suggestions?",
            icon: <ExclamationCircleOutlined />,
            content: "This action will replace your current mappings with new AI-generated suggestions. Are you sure you want to proceed?",
            onOk() {
                onGetAiMappingSuggestion(fileReadResult);
            },
        });
    }, [onGetAiMappingSuggestion, fileReadResult]);

    const handleResetAiMapping = React.useCallback(() => {
        aiSuggestionsAppliedRef.current = false;
        onResetAiMapping();
    }, [onResetAiMapping]);

    const validateAndUpload = React.useCallback(() => {
        let isValid = validateData();
        if (isValid && fileReadResult?.file && dataColumnMapping) {
            let fieldMappings: any[] = [];
            for (let field of schemaModel.fields) {
                if (dataColumnMapping[field.name]) {
                    fieldMappings.push({
                        ...dataColumnMapping[field.name],
                        fieldId: field.fieldId,
                    });
                }
            }
            // console.log("Uploading with mapping", fieldMappings);
            onCreateDataObjectsBulkUploadJob(dataUploadOptions, fileReadResult.file, fieldMappings);
        }
    }, [dataColumnMapping, dataUploadOptions, fileReadResult, onCreateDataObjectsBulkUploadJob, schemaModel.fields, validateData]);

    const onValidationErrorsClose = React.useCallback(() => {
        setValidationErrors([]);
    }, []);

    const onMappingChanged = React.useCallback(
        (mappedField: IDataMappingField) => {
            const colMapping = { ...dataColumnMapping };

            // Check that it has all the new data
            colMapping[mappedField.name] = {
                ...colMapping[mappedField.name],
                ...mappedField,
            };

            // Save for upload later
            setDataColumnMapping(colMapping);
        },
        [dataColumnMapping],
    );

    const handleLoadMappingConfiguration = React.useCallback(
        (mappingConfiguration: IMappingSchemaFields) => {
            if (mappingConfiguration && fileReadResult) {
                const orderedMapping: IMappingSchemaFields = {};
                for (const field of schemaModel.fields) {
                    if (mappingConfiguration[field.name]) {
                        orderedMapping[field.name] = mappingConfiguration[field.name];
                    }
                }

                for (const key of Object.keys(mappingConfiguration)) {
                    if (!orderedMapping[key]) {
                        orderedMapping[key] = mappingConfiguration[key];
                    }
                }
                setDataColumnMapping(orderedMapping);
            }
        },
        [fileReadResult, schemaModel.fields],
    );

    const onConnectionSelectionComplete = React.useCallback(
        (connection: ISystemConnection | null) => {
            if (!connection || !connection.fileReadResult) return;

            let file: RcFileExtension | null = connection.file ?? null;
            if (
                !file &&
                (connection.connectionType === "jdbc" ||
                    connection.connectionType === "snowflake" ||
                    connection.connectionType === "salesforce")
            ) {
                // For Salesforce the selected object name is carried as tableName (the object/field
                // picker emits { tableName: objectName, ... }); the dataset-service import job reads it.
                file = {
                    name: `${connection.connectionType}://${connection.connectionId}/${connection.fileReadResult?.tableName}`,
                    connectionId: connection.connectionId,
                    connectionType: connection.connectionType,
                    tableName: connection.fileReadResult?.tableName,
                } as RcFileExtension;
            }
            const result: IFileReadResult<RcFileExtension> = {
                file,
                data: connection.fileReadResult.data,
                columns: [],
                error: null,
                meta: { fields: [] },
            };
            if (Array.isArray(result.data) && result.data.length > 0) {
                const lastRow = result.data[result.data.length - 1];
                const vals = Object.values(lastRow);
                if (!vals.some((v) => v)) {
                    result.data.pop();
                }
            }

            if (Array.isArray(result.data) && result.data.length > 0) {
                result.columns = Object.keys(result.data[0]);
                result.meta = { fields: result.columns };
            } else if (connection.fileReadResult?.meta?.fields) {
                result.columns = connection.fileReadResult.meta.fields;
                result.meta = { fields: result.columns };
            }

            if (file) {
                file.connectionId = connection.connectionId;
                file.connectionType = connection.connectionType;
                file.bucket = connection.bucket;
            }

            readFileComplete(result);
        },
        [readFileComplete],
    );

    const sourceData = React.useMemo((): IFormulaParserDataInput[] => {
        if (fileReadResult?.data) {
            if (Array.isArray(fileReadResult.data[0])) {
                // type PapaparseDataNoHeader
                // Not supported at this point.
            } else {
                // type PapaparseDataWithHeader
                return fileReadResult.data as IFormulaParserDataInput[];
            }
        }
        return [];
    }, [fileReadResult]);

    // Remove the resizeObserver error due to rendering of ReactFlow
    React.useEffect(() => {
        const errorHandler = (e: any) => {
            if (e.message.includes("ResizeObserver loop completed with undelivered notifications") || e.message.includes("ResizeObserver loop limit exceeded")) {
                const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
                if (resizeObserverErr) {
                    resizeObserverErr.style.display = "none";
                }
            }
        };
        window.addEventListener("error", errorHandler);

        return () => {
            window.removeEventListener("error", errorHandler);
        };
    }, []);

    // Track if AI suggestions have been applied to prevent re-application
    const aiSuggestionsAppliedRef = React.useRef(false);

    React.useEffect(() => {
        if (aiMappingSuggestion && aiMappingSuggestion.data?.mappings && fileReadResult?.data && fileReadResult.data.length > 0 && !aiSuggestionsAppliedRef.current) {
            const aiMappings = aiMappingSuggestion.data.mappings;

            setDataColumnMapping((prevMapping) => {
                const newMapping: IMappingSchemaFields = { ...prevMapping };

                aiMappings.forEach((aiMapping: any) => {
                    // Find the target field in the schema
                    const targetField = Object.keys(newMapping).find((key) => newMapping[key].name === aiMapping.targetAttribute);

                    if (targetField) {
                        newMapping[targetField] = {
                            ...newMapping[targetField],
                            mappingType: "DATA_COLUMN" as IDataMappingType,
                            value: aiMapping.sourceColumn,
                            sourceColumns: [aiMapping.sourceColumn],
                        };
                    }
                });

                return newMapping;
            });

            aiSuggestionsAppliedRef.current = true;
        }
    }, [aiMappingSuggestion, fileReadResult]);

    React.useEffect(() => {}, [getAiMappingSuggestionResult]);

    const mappedDataColumns = React.useMemo((): string[] => {
        if (dataColumnMapping) {
            return Object.keys(dataColumnMapping).reduce((columns, key) => {
                if (dataColumnMapping?.[key]?.mappingType === "DATA_COLUMN" && dataColumnMapping?.[key]?.value) {
                    columns.push(dataColumnMapping?.[key]?.value || "");
                } else {
                    dataColumnMapping?.[key]?.variables?.forEach((v: any) => {
                        columns.push(v.value);
                    });
                }
                return columns;
            }, [] as string[]);
        } else {
            return [];
        }
    }, [dataColumnMapping]);

    const handleSampleRowChange = React.useCallback(
        (newRow: string) => {
            if (newRow && parseInt(newRow) > 0 && sourceData && parseInt(newRow) < sourceData.length + 1) {
                setSampleDataRow(parseInt(newRow) - 1);
            }
        },
        [sourceData],
    );

    // const dispatch = useAppDispatch();
    React.useEffect(() => {
        // Set the source Data and row for DataMapper and DataMapperFlow
        dispatch(setDataMapperFlowAction({ data: sourceData, row: sampleDataRow }));
    }, [dispatch, sourceData, sampleDataRow, showFlowMapper]);

    return (
        <DataObjectBulkUploadLayout>
            <Header>
                <>
                    <div className="flex flex-auto flex-col rounded-md bg-gray-100 p-2 px-4">
                        <p className="text-sm font-bold">
                            <span className="mr-1 font-bold text-rose-800">Schema:</span>
                            {schemaModel.name}
                        </p>
                        <p className="text-sm">{schemaModel.description}</p>
                    </div>
                    <div className="flex flex-auto flex-col rounded-md bg-gray-100 p-2 px-4">
                        <p className="text-sm font-bold">
                            <span className="mr-1 font-bold text-sky-800">Dataset:</span>
                            {dataSet.dataSetName}
                        </p>
                        <p className="text-sm">{dataSet.dataSetDescription}</p>
                    </div>
                </>
            </Header>

            {fileReadResult && dataColumnMapping && (
                <div className="flex flex-col items-start justify-start gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="inline-flex gap-2">
                        <div className="inline-flex flex-nowrap items-center rounded-md bg-slate-100">
                            <div className="inline-flex flex-row gap-2 px-3">
                                <p>Data Preview</p>
                                <Tooltip title={`Toggle to ${showDataTable ? "hide" : "show"} Data Preview`}>
                                    <Switch value={showDataTable} checkedChildren="ON" unCheckedChildren="OFF" onChange={(checked) => setShowDataTable(checked)} />
                                </Tooltip>
                            </div>
                            <Tooltip title="Dock Data Preview to the left">
                                <Button
                                    type="text"
                                    title="Dock to left"
                                    onClick={() => setDataPanelPosition("left")}
                                    className={mergeClassNames(
                                        "rounded-none border-l border-r border-l-slate-200 border-r-slate-200",
                                        dataPanelPosition === "left" ? "bg-slate-200" : "",
                                    )}
                                >
                                    <VerticalRightOutlined />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Dock Data Preview to the bottom">
                                <Button
                                    type="text"
                                    title="Dock to bottom"
                                    onClick={() => setDataPanelPosition("bottom")}
                                    className={mergeClassNames("rounded-none border-r border-r-slate-200", dataPanelPosition === "bottom" ? "bg-slate-200" : "")}
                                >
                                    <VerticalAlignBottomOutlined />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Dock Data Preview to the right">
                                <Button
                                    type="text"
                                    title="Dock to right"
                                    onClick={() => setDataPanelPosition("right")}
                                    className={mergeClassNames("rounded-l-none", dataPanelPosition === "right" ? "bg-slate-200" : "")}
                                >
                                    <VerticalLeftOutlined />
                                </Button>
                            </Tooltip>
                        </div>

                        {!!fileReadResult?.data && fileReadResult.data.length > 0 && (
                            <div className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-2">
                                <div className="whitespace-nowrap text-sm font-bold">Sample Data Row</div>
                                <Tooltip title={`Enter number between 1 to ${fileReadResult.data?.length}`}>
                                    <Input
                                        type="number"
                                        size="small"
                                        defaultValue={1}
                                        onChange={(e) => handleSampleRowChange(e.currentTarget.value)}
                                        min={1}
                                        max={fileReadResult.data.length}
                                    />
                                </Tooltip>
                            </div>
                        )}

                        {isUploadAllowed && (
                            <div className="flex items-center gap-2">
                                {onSaveMappingConfiguration && onGetMappingConfigurations && onDeleteMappingConfiguration && (
                                    <MappingConfigurationManager
                                        businessAreaId={schemaModel.businessAreaId}
                                        schemaId={schemaModel.schemaId}
                                        currentMapping={dataColumnMapping}
                                        mappingConfigurations={mappingConfigurations}
                                        onSaveMappingConfiguration={onSaveMappingConfiguration}
                                        onGetMappingConfigurations={onGetMappingConfigurations}
                                        onDeleteMappingConfiguration={onDeleteMappingConfiguration}
                                        onLoadMappingConfiguration={handleLoadMappingConfiguration}
                                        saveMappingConfigurationResult={saveMappingConfigurationResult}
                                        getMappingConfigurationsResult={getMappingConfigurationsResult}
                                        deleteMappingConfigurationResult={deleteMappingConfigurationResult}
                                    />
                                )}
                                <Tooltip title="Get AI mapping suggestions">
                                    <Button
                                        type="default"
                                        onClick={handleGetAiMappingSuggestion}
                                        disabled={!fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading}
                                        className="relative inline-flex h-12 overflow-hidden rounded-full border-none p-1 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50"
                                    >
                                        <span
                                            className={`absolute inset-[-1000%] animate-[spin_2s_linear_infinite] ${
                                                !fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading
                                                    ? "bg-[conic-gradient(from_90deg_at_50%_50%,#64748b_0%,#334155_50%,#64748b_100%)]"
                                                    : "bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]"
                                            }`}
                                        />

                                        <span
                                            className={`inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full px-6 py-2 text-sm font-medium backdrop-blur-3xl ${
                                                !fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading ? "bg-slate-600 text-slate-300" : "bg-slate-950 text-white"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {!aiMappingLoading && fileReadResult?.data && fileReadResult?.columns && (
                                                    <div className="absolute -inset-0.5 h-10 w-10 animate-ping rounded-full bg-indigo-400 opacity-30"></div>
                                                )}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48" className="relative z-10">
                                                    <path
                                                        fill={!fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading ? "#64748b" : "#babffc"}
                                                        d="M36.845,45.378l-0.494-1.132c-0.881-2.019-2.467-3.627-4.447-4.506l-1.522-0.676	c-0.823-0.366-0.823-1.562,0-1.928l1.437-0.639c2.03-0.902,3.645-2.569,4.511-4.657l0.507-1.224c0.354-0.853,1.533-0.853,1.886,0	l0.507,1.224c0.866,2.088,2.481,3.755,4.511,4.657l1.437,0.639c0.823,0.366,0.823,1.562,0,1.928	C45.178,39.064,41.286,38.15,36.845,45.378z"
                                                    />
                                                    <path
                                                        fill={!fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading ? "#475569" : "#324561"}
                                                        d="M37.799,45.979c-0.133,0-0.269-0.027-0.398-0.083c-0.506-0.221-0.738-0.81-0.518-1.316l0.493-1.132	c1.085-2.485,3.027-4.45,5.468-5.534l1.521-0.676c0.505-0.226,1.096,0.003,1.32,0.507c0.224,0.505-0.003,1.096-0.508,1.32	l-1.521,0.676c-1.983,0.881-3.562,2.481-4.447,4.506l-0.492,1.131C38.553,45.754,38.186,45.979,37.799,45.979z"
                                                    />
                                                    <path
                                                        fill={!fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading ? "#64748b" : "#babffc"}
                                                        d="M22.285,31.088l-1.16,4.493c-0.675,1.547-2.816,1.547-3.491,0l-1.757-4.024	c-1.564-3.581-4.378-6.432-7.888-7.99L3.153,21.42c-1.538-0.682-1.538-2.919,0-3.602l4.685-2.08	c3.601-1.598,6.465-4.554,8.002-8.258l1.78-4.288c0.66-1.591,2.859-1.591,3.52,0l1.78,4.288c1.537,3.703,4.402,6.659,8.002,8.258	l4.685,2.08c1.538,0.682,1.538,2.919,0,3.602l-5.433,1.678C26.663,24.657,23.849,27.507,22.285,31.088z"
                                                    />
                                                    <path
                                                        fill={!fileReadResult?.data || !fileReadResult?.columns || aiMappingLoading ? "#475569" : "#324561"}
                                                        d="M20.208,36.182c-0.133,0-0.269-0.027-0.399-0.084c-0.506-0.221-0.737-0.811-0.516-1.316l1.757-4.024	c1.768-4.049,4.932-7.252,8.909-9.018l4.836-2.147c0.511-0.227,1.096,0.004,1.32,0.508c0.224,0.505-0.004,1.096-0.508,1.32	l-4.836,2.147c-3.521,1.562-6.321,4.4-7.89,7.99l-1.757,4.024C20.961,35.957,20.594,36.182,20.208,36.182z"
                                                    />
                                                </svg>
                                                <span className="relative z-10 bg-gradient-to-r from-purple-400 via-blue-500 to-indigo-600 bg-clip-text text-base font-bold tracking-wide text-transparent drop-shadow-lg">
                                                    {aiMappingLoading ? "Getting AI Mapping..." : "AI Mapping"}
                                                </span>
                                            </div>
                                        </span>
                                    </Button>
                                </Tooltip>
                                {aiMappingSuggestion && (
                                    <Button type="default" onClick={handleResetAiMapping} disabled={aiMappingLoading} className="h-12">
                                        Reset AI Mapping
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row items-center gap-3">
                        <p>Try out the new Flow Mapper</p>
                        <ArrowRightOutlined className="animate-slide" />
                        <Switch value={showFlowMapper} checkedChildren="Flow Mapper" unCheckedChildren="Table Mapper" onChange={(checked) => setShowFlowMapper(checked)} />
                    </div>
                </div>
            )}

            <DockableContainer
                showPanel={!!fileReadResult && !!dataColumnMapping && showDataTable}
                panelPosition={dataPanelPosition}
                panel={
                    fileReadResult &&
                    dataColumnMapping && (
                        <DataPreview
                            title={
                                <>
                                    <Text>{`${fileReadResult?.file?.name ?? "No file name"} - `}</Text>
                                    <Text type="secondary">{`Data Preview (${fileReadResult?.data?.length ?? 0} Records)`}</Text>
                                </>
                            }
                            transposed={dataPanelPosition !== "bottom"}
                            data={(fileReadResult?.data as Record<string, React.ReactNode>[]) || []}
                            columns={fileReadResult?.columns || []}
                            dataSchemaColumns={mappedDataColumns || []}
                        />
                    )
                }
            >
                <>
                    {!!fileReadResult && !!dataColumnMapping && Object.values(dataColumnMapping).length > 0 ? (
                        <div className="scroll-webkit w-full flex-auto overflow-auto border border-slate-100">
                            {showFlowMapper ? (
                                <DataMapperFlow
                                    schemaFields={[...Object.values(dataColumnMapping)]}
                                    sourceFields={fileReadResult.columns ?? []}
                                    onMappingChanged={onMappingChanged}
                                    fullsize={!showDataTable}
                                    aiMappingSuggestions={aiMappingSuggestion?.data?.mappings || []}
                                />
                            ) : (
                                <DataMapper
                                    destinationFields={[...Object.values(dataColumnMapping)]}
                                    sourceFields={fileReadResult.columns ?? []}
                                    sourceData={sourceData}
                                    onMappingChanged={onMappingChanged}
                                    row={sampleDataRow}
                                    aiMappingSuggestions={aiMappingSuggestion?.data?.mappings || []}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="flex w-full flex-auto flex-col overflow-auto">
                            {validationErrors.length > 0 && (
                                <Alert
                                    title="Validation Error"
                                    description={
                                        <>
                                            {validationErrors.map((error, index) => (
                                                <p key={index}>{error}</p>
                                            ))}
                                        </>
                                    }
                                    type="error"
                                    closable={{
                                        onClose: onValidationErrorsClose,
                                    }}
                                />
                            )}
                            {bulkUploadSource === "systemconnection" ? (
                                <>
                                    {fileReadResult?.data && fileReadResult.data.length > 0 && dataColumnMapping ? (
                                        <DataPreview
                                            title={
                                                <>
                                                    <Text>{`${fileReadResult?.file?.name ?? "No file name"} - `}</Text>
                                                    <Text type="secondary">{`Data Preview (${fileReadResult?.data?.length ?? 0} Records)`}</Text>
                                                </>
                                            }
                                            data={(fileReadResult?.data as Record<string, React.ReactNode>[]) || []}
                                            columns={fileReadResult?.columns || []}
                                            dataSchemaColumns={Object.keys(dataColumnMapping || {}).reduce((columns, key) => {
                                                if (dataColumnMapping?.[key]?.mappingType === "DATA_COLUMN" && dataColumnMapping?.[key]?.value) {
                                                    columns.push(dataColumnMapping?.[key]?.value || "");
                                                }
                                                return columns;
                                            }, [] as string[])}
                                            onClose={cancelUpload}
                                            closeLabel="Cancel"
                                            closeTooltip="Cancel and upload another file"
                                        />
                                    ) : (
                                        <SystemConnectionSelector
                                            businessAreaId={schemaModel.businessAreaId}
                                            onConnectionSelectionComplete={onConnectionSelectionComplete}
                                        ></SystemConnectionSelector>
                                    )}
                                </>
                            ) : (
                                <FileDragger
                                    ref={draggerRef}
                                    onReadingFile={onReadingFile}
                                    onReadFileComplete={readFileComplete}
                                    onReadFileError={readFileError}
                                    onCancelUpload={cancelUpload}
                                    dataSchemaColumns={mappedSchemaColumns}
                                    showPreview={false}
                                ></FileDragger>
                            )}
                        </div>
                    )}
                </>
            </DockableContainer>
            <Footer>
                <div className="flex w-full flex-col gap-4">
                    {hasPrimaryKey && (
                        <div className="flex w-full">
                            <Alert
                                message="Note: If existing records are found (matched by Primary Key), updates will be applied according to the Field-Level Survivorship Rules configured in your Schema."
                                type="info"
                                showIcon
                                className="w-full"
                            />
                        </div>
                    )}
                    <div className="flex flex-1 flex-col gap-4 rounded-md bg-gray-100 p-2 md:flex-row md:items-center md:justify-end">
                        <Radio.Group defaultValue={dataUploadOptions} value={dataUploadOptions} onChange={(e) => setDataUploadOptions(e.target.value)}>
                            <div className="flex flex-col gap-2 md:flex-row">
                                <Tooltip
                                    title={
                                        isReplaceDisabled
                                            ? "REPLACE_DATA is not supported for datasets that have existing records and a Primary Key. Please use standard upload (APPEND mode)."
                                            : ""
                                    }
                                >
                                    <span style={isReplaceDisabled ? { display: "inline-block", cursor: "not-allowed" } : {}}>
                                        <Radio style={isReplaceDisabled ? { pointerEvents: "none" } : {}} disabled={isReplaceDisabled} value="REPLACE_DATA">
                                            Replace existing data in the dataset
                                        </Radio>
                                    </span>
                                </Tooltip>
                                <Radio value="APPEND_DATA">Append this data to the existing</Radio>
                            </div>
                        </Radio.Group>
                        {isUploadAllowed && (
                            <Button type="default" onClick={cancelUpload}>
                                Cancel
                            </Button>
                        )}
<Tooltip title={unmappedMandatoryFields.length > 0 ? `Required fields not mapped: ${unmappedMandatoryFields.join(", ")}` : undefined}>
                            <span>
                                <Button type="primary" disabled={!isUploadAllowed || unmappedMandatoryFields.length > 0} onClick={validateAndUpload}>
                                    Verify and Upload
                                </Button>
                            </span>
                        </Tooltip>
                    </div>{" "}
                </div>{" "}
            </Footer>
        </DataObjectBulkUploadLayout>
    );
}